import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { VideoItem } from './source';

interface Props {
    videos: VideoItem[];
    activeVideoId: string | null;
    playingVideoId: string | null;
    onSelect: (video: VideoItem) => void;
    height?: number;
}

const ScrollableVideos: React.FC<Props> = ({ videos, activeVideoId, playingVideoId, onSelect, height }) => {
    return (
        <Box
            sx={{
                borderRadius: '16px',
                boxShadow: '0 0 12px rgba(0,0,0,0.3)',
                backgroundColor: { xs: '#F6F8FD', md: '#fff' },
                height: { xs: '100%', md: height || '100%' },
                padding: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 1,
                    height: '100%',
                    width: '100%',
                    overflowX: { xs: 'auto', md: 'hidden' },
                    overflowY: { xs: 'hidden', md: 'auto' },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    scrollSnapType: { xs: 'x mandatory', md: undefined },
                    scrollPadding: '8px',
                    paddingRight: { xs: 0, md: 1 },
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                        display: { xs: 'none', md: 'block' },
                    },
                    '-ms-overflow-style': { xs: 'none', md: 'auto' },
                    'scrollbar-width': { xs: 'none', md: 'auto' },
                }}
                className='styled-scrollbar'
            >
                {videos.map((video) => {
                    const isActive = video.id === activeVideoId;
                    const isPlaying = video.id === playingVideoId;

                    return (
                        <Box
                            key={video.id}
                            sx={{
                                flex: { xs: '0 0 140px', md: '0 0 auto' },
                                mb: 1,
                                scrollSnapAlign: { xs: 'start', md: undefined },
                                p: 1,
                                borderRadius: '8px',
                                boxShadow: '0px 1px 8px rgba(0,0,0,0.2)',
                                border: '1px solid rgba(0,0,0,0.05)',
                                cursor: 'pointer',
                                width: { xs: '140px', md: '100%' },
                                position: 'relative',
                                backgroundColor: { xs: '#fff', md: isActive ? '#CCD8E6' : '#fff' },
                                WebkitTapHighlightColor: 'transparent',
                            }}
                            onClick={() => onSelect(video)}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    paddingTop: '56.25%',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                }}
                            >
                                <img
                                    src={video.thumbnail}
                                    alt={video.title}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '8px',
                                        backgroundColor: isActive
                                            ? 'rgba(0, 0, 0, 0.5)'
                                            : 'rgba(164, 154, 154, 0.1)',
                                    }}
                                />
                                {isActive && (
                                    <Box
                                        component="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSelect(video);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            color: '#fff',
                                            backgroundColor: '#000',
                                            border: '3px solid rgba(221, 218, 218, 0.3)',
                                            boxShadow: '0 0 8px rgba(0, 0, 0, 0.6)',
                                            borderRadius: '25px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: { xs: '0.1rem 0rem', md: '0.3rem 0.2rem' },
                                            gap: 0.5,
                                            width: '80%',
                                            WebkitTapHighlightColor: 'transparent',
                                        }}
                                    >
                                        <Typography variant="subtitle2" fontWeight={600} fontSize={{ xs: '9px', md: '9.5px', lg: '12px' }}>
                                            {isPlaying ? 'Now Playing' : 'Click to Play'}
                                        </Typography>
                                        {isPlaying ? <PauseIcon sx={{ fontSize: { xs: '0.7rem', md: '0.6rem', lg: '1rem' } }} /> : <PlayArrowIcon sx={{ fontSize: { xs: '0.7rem', md: '0.6rem', lg: '1rem' } }} />}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ mt: 1, pl: 0.5, minHeight: 20 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    fontSize={{ xs: '9.5px', md: '11.5px', lg: '13px' }}
                                    lineHeight="1.3em"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        height: '1.3em',
                                        color: '#1D2935'
                                    }}
                                >
                                    {video.title}
                                </Typography>

                                <Typography
                                    variant="subtitle2"
                                    fontWeight={400}
                                    fontSize={{ xs: '8px', md: '11px', lg: '12px' }}
                                    pt={0.5}
                                    color='#3A5269'
                                >
                                    {video.duration}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box >
    );
};

export default ScrollableVideos;
